// Anordnung der Topologie-Karte.
//
// Netzwerke sind fast immer Bäume mit ein paar Querverbindungen: Firewall
// oben, Core darunter, Access-Switches und Access Points darunter. Deshalb
// eine Anordnung in Ebenen statt einer Kräftesimulation: sie ist bei jedem
// Aufruf gleich (die Karte springt beim Auffrischen nicht), braucht keine
// Bibliothek und lässt sich lesen wie ein Plan im Rack-Schrank.
import type { Topology, TopologyNode } from './types'

export const KNOTEN_BREITE = 184
export const KNOTEN_HOEHE = 58
const ABSTAND_X = 28
const ABSTAND_Y = 92
const ABSTAND_GRUPPE = 72
const RAND = 24

/** Ein Punkt der Karte: ein verwaltetes Gerät oder ein unbekannter Nachbar. */
export interface KartenKnoten {
  id: string
  x: number
  y: number
  /** Gesetzt für verwaltete Geräte. */
  geraet?: TopologyNode
  /** Name eines Nachbarn, den die Netzwarte nicht kennt. */
  fremdName?: string
}

export interface KartenVerbindung {
  a: string
  b: string
  /** Ports an beiden Enden, soweit LLDP sie nennt. */
  portA?: string
  portB?: string
  /** lldp: gemeldet; abhaengig: nur als übergeordnetes Gerät eingetragen. */
  art: 'lldp' | 'abhaengig'
  /** Zustand der Leitung aus den Ports beider Enden, der schlechtere zählt. */
  stufe?: 'ok' | 'warn' | 'crit'
  link?: 'up' | 'down'
  /** Höchste Auslastung in Prozent und die Werte dazu. */
  auslastung?: number
  bps?: number
  mbit?: number
}

const STUFEN_RANG = { ok: 1, warn: 2, crit: 3 } as const

/** Übernimmt den Zustand einer gemeldeten Kante in die Linie. */
function zustandUebernehmen(v: KartenVerbindung, k: Topology['kanten'][number]): void {
  if (k.stufe && (!v.stufe || STUFEN_RANG[k.stufe] > STUFEN_RANG[v.stufe])) v.stufe = k.stufe
  if (k.link === 'down' || (k.link && !v.link)) v.link = k.link
  if (k.auslastung !== undefined && (v.auslastung === undefined || k.auslastung > v.auslastung)) {
    v.auslastung = k.auslastung
    v.bps = Math.max(k.ein_bps ?? 0, k.aus_bps ?? 0)
    v.mbit = k.mbit
  }
}

export interface KartenLayout {
  knoten: KartenKnoten[]
  verbindungen: KartenVerbindung[]
  breite: number
  hoehe: number
}

/** Kennung eines unbekannten Nachbarn; derselbe Name ist derselbe Nachbar. */
export function fremdId(name: string): string {
  return `fremd:${name.trim().toLowerCase()}`
}

/** Punkte und Linien der Karte, ohne Koordinaten. */
export function kartenGraph(topo: Topology): {
  knoten: Map<string, Omit<KartenKnoten, 'x' | 'y'>>
  verbindungen: KartenVerbindung[]
} {
  const knoten = new Map<string, Omit<KartenKnoten, 'x' | 'y'>>()
  for (const g of topo.knoten) knoten.set(g.id, { id: g.id, geraet: g })

  // LLDP meldet eine Leitung meist von beiden Enden. Eine Linie je Paar,
  // die Ports aus beiden Meldungen zusammengeführt.
  const paare = new Map<string, KartenVerbindung>()
  for (const k of topo.kanten) {
    if (!knoten.has(k.von)) continue
    let ziel = k.verwaltet && k.zu ? k.zu : ''
    if (!ziel) {
      if (!k.zu_name.trim()) continue
      ziel = fremdId(k.zu_name)
      if (!knoten.has(ziel)) knoten.set(ziel, { id: ziel, fremdName: k.zu_name.trim() })
    }
    if (ziel === k.von) continue
    const [a, b] = k.von < ziel ? [k.von, ziel] : [ziel, k.von]
    const schluessel = `${a}|${b}`
    const v = paare.get(schluessel) ?? { a, b, art: 'lldp' as const }
    const [eigen, fremd] = k.von === a ? (['portA', 'portB'] as const) : (['portB', 'portA'] as const)
    v[eigen] ??= k.von_port || undefined
    v[fremd] ??= k.zu_port || undefined
    zustandUebernehmen(v, k)
    paare.set(schluessel, v)
  }
  // Ein übergeordnetes Gerät ohne gemeldete Leitung ist trotzdem verbunden –
  // oft genau dort, wo LLDP fehlt (Firewall vor dem Core).
  for (const g of topo.knoten) {
    if (!g.uebergeordnet || !knoten.has(g.uebergeordnet)) continue
    const [a, b] = g.id < g.uebergeordnet ? [g.id, g.uebergeordnet] : [g.uebergeordnet, g.id]
    if (!paare.has(`${a}|${b}`)) paare.set(`${a}|${b}`, { a, b, art: 'abhaengig' })
  }
  return { knoten, verbindungen: [...paare.values()] }
}

const RANG_ART: Record<string, number> = {
  firewall: 3,
  router: 3,
  gateway: 3,
  switch: 1,
}

function name(k: Omit<KartenKnoten, 'x' | 'y'>): string {
  return (k.geraet?.name ?? k.fremdName ?? k.id).toLowerCase()
}

/**
 * Ordnet die Karte an. Je zusammenhängender Gruppe beginnt oben das Gerät,
 * von dem andere abhängen (sonst Firewall/Router, sonst das mit den meisten
 * Leitungen); darunter folgen die Ebenen nach Abstand. Geräte ohne jede
 * Leitung stehen am Ende in einer eigenen Reihe.
 */
export function anordnen(topo: Topology): KartenLayout {
  const { knoten, verbindungen } = kartenGraph(topo)
  const nachbarn = new Map<string, Set<string>>()
  for (const id of knoten.keys()) nachbarn.set(id, new Set())
  for (const v of verbindungen) {
    nachbarn.get(v.a)!.add(v.b)
    nachbarn.get(v.b)!.add(v.a)
  }
  const kinder = new Map<string, number>()
  for (const g of topo.knoten) {
    if (g.uebergeordnet && knoten.has(g.uebergeordnet)) {
      kinder.set(g.uebergeordnet, (kinder.get(g.uebergeordnet) ?? 0) + 1)
    }
  }
  const wurzelWert = (id: string): number[] => {
    const k = knoten.get(id)!
    const g = k.geraet
    const istKind = g?.uebergeordnet && knoten.has(g.uebergeordnet) ? 1 : 0
    return [g ? 1 : 0, istKind ? 0 : (kinder.get(id) ?? 0), RANG_ART[g?.typ ?? ''] ?? 0, nachbarn.get(id)!.size]
  }
  const besser = (x: string, y: string): boolean => {
    const a = wurzelWert(x)
    const b = wurzelWert(y)
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i]! > b[i]!
    return name(knoten.get(x)!) < name(knoten.get(y)!)
  }

  const ids = [...knoten.keys()].sort((x, y) => name(knoten.get(x)!).localeCompare(name(knoten.get(y)!)))
  const gesehen = new Set<string>()
  const gruppen: string[][][] = []
  const einzeln: string[] = []
  for (const start of ids) {
    if (gesehen.has(start)) continue
    if (nachbarn.get(start)!.size === 0) {
      gesehen.add(start)
      einzeln.push(start)
      continue
    }
    // Die ganze Gruppe einsammeln, dann ihre Wurzel bestimmen.
    const gruppe: string[] = []
    const stapel = [start]
    gesehen.add(start)
    while (stapel.length) {
      const id = stapel.pop()!
      gruppe.push(id)
      for (const n of nachbarn.get(id)!) {
        if (!gesehen.has(n)) {
          gesehen.add(n)
          stapel.push(n)
        }
      }
    }
    const wurzel = gruppe.reduce((w, id) => (besser(id, w) ? id : w))
    // Ebenen nach Abstand von der Wurzel; innerhalb einer Ebene nach der
    // mittleren Lage der Nachbarn darüber, damit sich Linien selten kreuzen.
    const ebene = new Map<string, number>([[wurzel, 0]])
    const ebenen: string[][] = [[wurzel]]
    for (let i = 0; i < ebenen.length; i++) {
      const naechste: string[] = []
      for (const id of ebenen[i]!) {
        const sortiert = [...nachbarn.get(id)!].sort((x, y) => name(knoten.get(x)!).localeCompare(name(knoten.get(y)!)))
        for (const n of sortiert) {
          if (!ebene.has(n)) {
            ebene.set(n, i + 1)
            naechste.push(n)
          }
        }
      }
      if (naechste.length) {
        const lage = new Map(ebenen[i]!.map((id, pos) => [id, pos]))
        const mitte = (id: string) => {
          const oben = [...nachbarn.get(id)!].filter((n) => lage.has(n)).map((n) => lage.get(n)!)
          return oben.reduce((s, p) => s + p, 0) / Math.max(oben.length, 1)
        }
        // Verwaltete Geräte vor unbekannten Nachbarn desselben Elternteils.
        naechste.sort(
          (x, y) =>
            mitte(x) - mitte(y) || fremdZuletzt(x, y) || name(knoten.get(x)!).localeCompare(name(knoten.get(y)!)),
        )
        ebenen.push(naechste)
      }
    }
    gruppen.push(ebenen)
  }

  function fremdZuletzt(x: string, y: string): number {
    return (knoten.get(x)!.geraet ? 0 : 1) - (knoten.get(y)!.geraet ? 0 : 1)
  }

  const platziert: KartenKnoten[] = []
  let x0 = RAND
  let hoehe = 0
  for (const ebenen of gruppen) {
    const breiteste = Math.max(...ebenen.map((e) => e.length))
    const gruppenBreite = breiteste * KNOTEN_BREITE + (breiteste - 1) * ABSTAND_X
    ebenen.forEach((e, stufe) => {
      const reihe = e.length * KNOTEN_BREITE + (e.length - 1) * ABSTAND_X
      const links = x0 + (gruppenBreite - reihe) / 2
      e.forEach((id, i) => {
        platziert.push({
          ...knoten.get(id)!,
          x: links + i * (KNOTEN_BREITE + ABSTAND_X),
          y: RAND + stufe * (KNOTEN_HOEHE + ABSTAND_Y),
        })
      })
    })
    hoehe = Math.max(hoehe, RAND + ebenen.length * (KNOTEN_HOEHE + ABSTAND_Y) - ABSTAND_Y)
    x0 += gruppenBreite + ABSTAND_GRUPPE
  }
  let breite = gruppen.length ? x0 - ABSTAND_GRUPPE + RAND : 0

  if (einzeln.length) {
    // So viele je Reihe, wie in die bisherige Breite passen, mindestens vier.
    const proReihe = Math.max(4, Math.floor((breite - 2 * RAND + ABSTAND_X) / (KNOTEN_BREITE + ABSTAND_X)))
    const y0 = gruppen.length ? hoehe + ABSTAND_Y : RAND
    einzeln.forEach((id, i) => {
      platziert.push({
        ...knoten.get(id)!,
        x: RAND + (i % proReihe) * (KNOTEN_BREITE + ABSTAND_X),
        y: y0 + Math.floor(i / proReihe) * (KNOTEN_HOEHE + ABSTAND_X),
      })
    })
    const reihen = Math.ceil(einzeln.length / proReihe)
    hoehe = y0 + reihen * (KNOTEN_HOEHE + ABSTAND_X) - ABSTAND_X
    breite = Math.max(breite, 2 * RAND + Math.min(einzeln.length, proReihe) * (KNOTEN_BREITE + ABSTAND_X) - ABSTAND_X)
  }

  return { knoten: platziert, verbindungen, breite, hoehe: hoehe + RAND }
}
